import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api_deleteVendor, api_getVendors, api_getVendorsByFilter } from '../../../api/vendor';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Input from '../../../components/bootstrap/forms/Input';
import Icon from '../../../components/icon/Icon';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import Button from '../../../components/bootstrap/Button';

const PAGE_SIZE = 5;

const Item = ({ item, removeVendorFromTable, index }: any) => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const handleDeleteVendor = async (vendorId: string) => {
		try {
			setLoading(true);
			const res = await api_deleteVendor(vendorId);
			if (res.isSuccess) {
				removeVendorFromTable(vendorId);
			} else {
				console.error('Failed to delete vendor:', res.message);
			}
		} catch (error) {
			console.error('Error deleting vendor:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<tr key={index}>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'></div>
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{item?.name}</div>
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{item?.location}</div>
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{item?.phoneNumber}</div>
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{item?.type}</div>
					</div>
				</div>
			</td>
			<td>
				<span
					onClick={() => navigate('/edit/vendor', { state: item })}
					style={{ margin: 10, cursor: 'pointer' }}>
					EDIT
				</span>
				<span
					onClick={() => handleDeleteVendor(item._id)}
					style={{ margin: 10, cursor: 'pointer' }}>
					{loading ? 'Loading..' : 'DELETE'}
				</span>
			</td>
		</tr>
	);
};

const AllVendor = () => {
	const [vendor, setVendor] = useState<any>([]);
	const [loading, setLoading] = useState(false);
	const [pageNo, setPageNo] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const navigate = useNavigate();
	const tableBodyRef = useRef<HTMLTableSectionElement>(null);
	const [filter, setFilter] = useState(false);
	const [searchString, setSearchString] = useState('');

	const getVendor = async (page_no?: number) => {
		const finalPageNo = page_no || pageNo;

		try {
			setLoading(true);
			const res = await api_getVendors(finalPageNo, PAGE_SIZE);

			if (res.isSuccess) {
				if (!res.data.length) {
					setHasMore(false);
				} else {
					if (page_no === 1) {
						setVendor(res.data);
						setPageNo(2);
					} else {
						setVendor([...vendor, ...res.data]);
						setPageNo(pageNo + 1);
					}
				}
			} else {
				console.error('Failed to fetch vendors:', res.message);
			}
		} catch (error) {
			console.error('Error fetching vendors:', error);
		} finally {
			setLoading(false);
		}
	};
	const getVendorByFilter = async () => {
		try {
			setLoading(true);
			setFilter(true);
			setVendor([]);
			const res = await api_getVendorsByFilter(searchString);

			if (res.isSuccess) {
				if (!res.data.length) {
					setHasMore(false);
					return;
				}
				setVendor(res.data);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const removeVendorFromTable = (vendorId: string) => {
		setVendor(vendor.filter((v: any) => v._id !== vendorId));
	};

	const onLoadMore = () => {
		if (hasMore) {
			getVendor();
		}
	};

	const handleFilterReset = () => {
		setVendor([]);
		setHasMore(true);
		getVendor(1);
	};

	useEffect(() => {
		getVendor();
	}, []);

	useEffect(() => {
		tableBodyRef.current?.addEventListener('scroll', (e) => {
			console.log(e);
		});
	}, []);

	return (
		<PageWrapper title={'Vendors'}>
			<div style={{ marginTop: '20px' }}>
				<SubHeader>
					<SubHeaderLeft>
						<label
							className='border-0 bg-transparent cursor-pointer me-0'
							htmlFor='searchInput'>
							<Icon
								icon='Search'
								size='2x'
								color='primary'
								onClick={() => {
									setVendor([]);
									setHasMore(true);
									getVendorByFilter();
								}}
							/>
						</label>
						<Input
							value={searchString}
							onChange={(e: any) => setSearchString(e?.target?.value)}
							id='searchInput'
							type='search'
							className='border-0 shadow-none bg-transparent'
							placeholder='Search user...'
						/>
					</SubHeaderLeft>
				</SubHeader>
			</div>
			<Card stretch>
				<CardHeader borderSize={1}>
					<CardLabel icon='Alarm' iconColor='info'>
						<CardTitle>All Vendors</CardTitle>
					</CardLabel>
					<CardActions>
						<Button
							onClick={() => navigate('/add/vendor')}
							icon='PersonAdd'
							color='primary'
							isLight={true}>
							New Vendor
						</Button>
					</CardActions>
				</CardHeader>
				<CardBody isScrollable className='table-responsive'>
					<table className='table table-modern'>
						<thead>
							<tr>
								<td style={{ width: 60 }} />
								<th>Name</th>
								<th>Location</th>
								<th>Phone Number</th>
								<th>Type</th>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{vendor?.map((item: any, index: any) => (
								<Item
									key={index}
									item={item}
									index={index}
									removeVendorFromTable={removeVendorFromTable}
								/>
							))}
						</tbody>
					</table>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							marginBottom: '20px',
						}}>
						{hasMore && (
							<button
								style={{
									border: 'none',
									padding: '10px',
									borderRadius: '10px',
									cursor: 'pointer',
								}}
								onClick={onLoadMore}>
								Load More...
							</button>
						)}
					</div>
				</CardBody>
			</Card>
		</PageWrapper>
	);
};

export default AllVendor;
